import { useQuery } from "@apollo/client";
import Refresh from "@mui/icons-material/Refresh";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import { Box, Grid, Typography, useTheme } from "@mui/material";
import { Dialog } from "components/Dialog";
import CopyableTypography from "components/Typography/CopyableTypography";
import { S3FileLink } from "components/S3FileLink";
import { StaffPipelineDialogQuery } from "generated-graphql/graphql";
import { prettyPrintDateTime } from "util/dates";
import { STAFF_PIPELINE_DIALOG_QUERY } from "./api";

export function PipelineDialog({
  pipelineId,
  onClose,
}: {
  pipelineId: string;
  onClose: () => void;
}) {
  const { data, refetch, loading } = useQuery<StaffPipelineDialogQuery>(
    STAFF_PIPELINE_DIALOG_QUERY,
    {
      variables: { id: pipelineId },
    }
  );
  const theme = useTheme();

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="lg"
      title={
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <span>Pipeline Details</span>
          <IconButton
            color="info"
            aria-label="Refresh pipeline details"
            onClick={() => refetch()}
            disabled={loading}
            size="small"
          >
            {loading ? <CircularProgress size={20} /> : <Refresh />}
          </IconButton>
        </Box>
      }
      noPrimaryAction
    >
      <Grid
        container
        spacing={theme.spacing(2)}
        sx={{ paddingBottom: theme.spacing(2) }}
      >
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="subtitle1" fontWeight="medium">
            Pipeline ID:
          </Typography>
          <CopyableTypography variant="body2" text={data?.pipeline.id} />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="subtitle1" fontWeight="medium">
            Name:
          </Typography>
          <Typography variant="body2">{data?.pipeline.name}</Typography>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="subtitle1" fontWeight="medium">
            Tenant:
          </Typography>
          <Typography variant="body2">
            {data?.pipeline.tenant?.name ?? ""}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="subtitle1" fontWeight="medium">
            Last Run:
          </Typography>
          <Typography variant="body2">
            {data?.pipeline.lastRunAt
              ? prettyPrintDateTime(data.pipeline.lastRunAt)
              : "Never"}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="subtitle1" fontWeight="medium">
            Created:
          </Typography>
          <Typography variant="body2">
            {prettyPrintDateTime(data?.pipeline.createdAt)}
          </Typography>
          <Typography variant="body2">{data?.pipeline.createdBy}</Typography>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="subtitle1" fontWeight="medium">
            Updated:
          </Typography>
          <Typography variant="body2">
            {prettyPrintDateTime(data?.pipeline.updatedAt)}
          </Typography>
          <Typography variant="body2">{data?.pipeline.updatedBy}</Typography>
        </Grid>
        <Grid size={12}>
          <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1 }}>
            Input Files ({data?.pipeline.inputFiles?.length ?? 0})
          </Typography>
          {data?.pipeline.inputFiles && data.pipeline.inputFiles.length > 0 ? (
            <Box>
              {data.pipeline.inputFiles.map((file, index) => (
                <S3FileLink key={index} fileKey={file} />
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No input files
            </Typography>
          )}
        </Grid>
        <Grid size={12}>
          <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1 }}>
            Output Files ({data?.pipeline.outputFiles?.length ?? 0})
          </Typography>
          {data?.pipeline.outputFiles &&
          data.pipeline.outputFiles.length > 0 ? (
            <Box>
              {data.pipeline.outputFiles.map((file, index) => (
                <S3FileLink key={index} fileKey={file} />
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No output files
            </Typography>
          )}
        </Grid>
      </Grid>
    </Dialog>
  );
}
